import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, Calculator, TrendingUp, AlertCircle, CheckCircle2, XCircle, Search, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StockValuationCalculator({ user, onLogout }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('TCS');
  const [currentPrice, setCurrentPrice] = useState(3500);
  const [eps, setEps] = useState(120); // Earnings per share
  const [bookValue, setBookValue] = useState(280); // Book value per share
  const [industryPE, setIndustryPE] = useState(28);
  const [growthRate, setGrowthRate] = useState(18); // % annual growth
  const [roe, setRoe] = useState(42); // Return on Equity %
  
  // Company search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [dataSource, setDataSource] = useState('Manual Input');
  
  // Clear selection and reset
  const clearSelection = () => {
    setSelectedSymbol('');
    setCompanyName('TCS');
    setSearchQuery('');
    setLastUpdated('');
    setDataSource('Manual Input');
    searchCompanies(''); // Reload all companies
  };
  
  // Search companies
  const searchCompanies = async (query = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/companies/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data.companies || []);
    } catch (error) {
      console.error('Error searching companies:', error);
      setSearchResults([]);
    }
  };
  
  // Fetch company data
  const fetchCompanyData = async (symbol) => {
    setIsLoadingData(true);
    
    // Clear old data immediately to prevent showing wrong calculations
    setCurrentPrice(0);
    setEps(0);
    setBookValue(0);
    setRoe(0);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/company-data/${symbol}`);
      const data = await response.json();
      
      if (data.success) {
        // Small delay to ensure clean transition
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Populate form with live data
        setCompanyName(data.company_name);
        setCurrentPrice(data.data.current_price || 100);
        setEps(data.data.eps || 10);
        setBookValue(data.data.book_value || 50);
        setIndustryPE(data.data.industry_pe || 25);
        setGrowthRate(data.data.growth_rate || 12);
        setRoe(data.data.roe || 15);
        setSelectedSymbol(data.symbol);
        setLastUpdated(new Date(data.last_updated).toLocaleString());
        setDataSource(`${data.source}`);
        setSearchQuery(''); // Clear search to hide dropdown
        
        // Show success notification
        alert(`✅ Loaded verified data for ${data.company_name}\n\nData Source: ${data.source}`);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      alert(`❌ Could not load data. This company may not be in our verified database yet.\n\nPlease use manual entry.`);
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Handle company selection
  const handleCompanySelect = (company) => {
    setSearchQuery(company.display);
    setShowDropdown(false);
    fetchCompanyData(company.symbol);
  };
  
  // Load all companies on mount for easy discovery
  useEffect(() => {
    searchCompanies(''); // Load all companies initially
  }, []);
  
  // Handle search input change
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      searchCompanies(searchQuery);
    }, 300);
    
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);
  
  // Calculate various valuation metrics
  const calculateValuation = () => {
    // Input validation
    if (eps <= 0 || bookValue <= 0 || currentPrice <= 0) {
      return {
        error: 'EPS, Book Value, and Current Price must be positive numbers',
        currentPE: '0',
        currentPB: '0',
        pegRatio: '0',
        fairValuePE: 0,
        fairValuePEG: 0,
        grahamNumber: 0,
        avgFairValue: 0,
        upsidePercent: '0',
        rating: 'Error',
        ratingColor: 'red'
      };
    }
    
    if (growthRate < 0) {
      return {
        error: 'Growth Rate cannot be negative for valuation calculations',
        currentPE: '0',
        currentPB: '0',
        pegRatio: '0',
        fairValuePE: 0,
        fairValuePEG: 0,
        grahamNumber: 0,
        avgFairValue: 0,
        upsidePercent: '0',
        rating: 'Error',
        ratingColor: 'red'
      };
    }
    
    // P/E Ratio
    const currentPE = currentPrice / eps;
    
    // P/B Ratio
    const currentPB = currentPrice / bookValue;
    
    // PEG Ratio (P/E to Growth) - handle zero growth
    const pegRatio = growthRate > 0 ? currentPE / growthRate : 0;
    
    // Fair Value based on Industry P/E
    const fairValuePE = eps * industryPE;
    
    // Fair Value based on PEG (ideal PEG = 1) - only if growth > 0
    const idealPE = growthRate > 0 ? growthRate * 1 : industryPE; // fallback to industry PE if no growth
    const fairValuePEG = eps * idealPE;
    
    // Graham Number (Conservative valuation) - Benjamin Graham's formula
    const grahamNumber = Math.sqrt(22.5 * eps * bookValue);
    
    // Upside/Downside - Average of three methods
    const avgFairValue = (fairValuePE + fairValuePEG + grahamNumber) / 3;
    const upsidePercent = ((avgFairValue - currentPrice) / currentPrice) * 100;
    
    // Valuation Rating
    let rating = 'Fair';
    let ratingColor = 'yellow';
    if (upsidePercent > 20) {
      rating = 'Undervalued';
      ratingColor = 'green';
    } else if (upsidePercent < -15) {
      rating = 'Overvalued';
      ratingColor = 'red';
    }
    
    return {
      error: null,
      currentPE: currentPE.toFixed(2),
      currentPB: currentPB.toFixed(2),
      pegRatio: pegRatio > 0 ? pegRatio.toFixed(2) : 'N/A',
      fairValuePE: Math.round(fairValuePE),
      fairValuePEG: Math.round(fairValuePEG),
      grahamNumber: Math.round(grahamNumber),
      avgFairValue: Math.round(avgFairValue),
      upsidePercent: upsidePercent.toFixed(1),
      rating,
      ratingColor
    };
  };

  const result = calculateValuation();

  const getRatingIcon = () => {
    if (result.ratingColor === 'green') return <CheckCircle2 className="w-6 h-6 text-green-600" />;
    if (result.ratingColor === 'red') return <XCircle className="w-6 h-6 text-red-600" />;
    return <AlertCircle className="w-6 h-6 text-yellow-600" />;
  };

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Stock Valuation Tool</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">
            Quick valuation check using multiple methods (P/E, PEG, Graham Number)
          </p>
        </div>

        {/* Compact Disclaimer */}
        <Card className="glass p-3 mb-6 dark:bg-gray-800 border-l-4 border-blue-500 bg-blue-50/30 dark:bg-blue-900/10">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-300">Educational Tool:</span> This calculator provides approximate valuations for learning purposes. Always verify data from reliable sources and consult financial advisors before making investment decisions.
          </div>
        </Card>

        {/* Company Search */}
        <Card className="glass p-4 mb-6 dark:bg-gray-800 border-l-4 border-green-500">
          <div className="flex items-start space-x-3">
            <Search className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  🚀 Quick Start: Select a Company with Verified Data
                </p>
                {selectedSymbol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="text-xs"
                  >
                    ← Change Company
                  </Button>
                )}
              </div>
              
              {/* Visible Company Chips */}
              {!selectedSymbol && (
              <div className="mb-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">
                  Click any company to auto-load financial data:
                </p>
                <div className="flex flex-wrap gap-2">
                  {searchResults.slice(0, 10).map((company) => (
                    <button
                      key={company.symbol}
                      onClick={() => handleCompanySelect(company)}
                      disabled={isLoadingData}
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full font-medium transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              </div>
              )}
              
              {/* Current Selection Display */}
              {selectedSymbol && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                    ✅ Selected: {companyName}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Current Market Price: ₹{currentPrice.toLocaleString('en-IN')}
                  </p>
                </div>
              )}
              
              {/* Search box for filtering (optional) */}
              {!selectedSymbol && (
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Or type to search/filter companies..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="dark:bg-gray-700 dark:text-gray-100 text-sm"
                  disabled={isLoadingData}
                />
              </div>
              )}
              
              {lastUpdated && (
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">Data Source:</span> {dataSource} | 
                  <span className="font-semibold ml-2">Last Updated:</span> {lastUpdated}
                </div>
              )}
              
              {isLoadingData && (
                <div className="mt-2 flex items-center text-sm text-blue-600 dark:text-blue-400">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading verified data...
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Inputs */}
          <div className="lg:col-span-1">
            <Card className="glass p-6 dark:bg-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Stock Details</h2>
                {selectedSymbol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchCompanyData(selectedSymbol)}
                    className="text-xs"
                    disabled={isLoadingData}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Refresh
                  </Button>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Company Name
                  </label>
                  <Input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Current Market Price (₹)
                  </label>
                  <Input
                    type="number"
                    value={currentPrice}
                    onChange={(e) => setCurrentPrice(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    EPS - Earnings Per Share (₹)
                  </label>
                  <Input
                    type="number"
                    value={eps}
                    onChange={(e) => setEps(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Available on Screener.in or NSE website
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Book Value Per Share (₹)
                  </label>
                  <Input
                    type="number"
                    value={bookValue}
                    onChange={(e) => setBookValue(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Net worth per share
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Industry Average P/E
                  </label>
                  <Input
                    type="number"
                    value={industryPE}
                    onChange={(e) => setIndustryPE(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Compare with peers in same sector
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Expected Growth Rate (%)
                  </label>
                  <Input
                    type="number"
                    value={growthRate}
                    onChange={(e) => setGrowthRate(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Historical EPS growth rate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    ROE - Return on Equity (%)
                  </label>
                  <Input
                    type="number"
                    value={roe}
                    onChange={(e) => setRoe(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Good companies: ROE &gt; 15%
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Error Display - Only show when not loading */}
            {result.error && !isLoadingData && (
              <Card className="glass p-6 dark:bg-gray-800 border-2 border-red-500">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">Validation Error</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{result.error}</p>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Overall Rating */}
            {!result.error && (
              <Card className={`glass p-6 dark:bg-gray-800 border-2 ${
                result.ratingColor === 'green' ? 'border-green-500' :
                result.ratingColor === 'red' ? 'border-red-500' :
                'border-yellow-500'
              }`}>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Valuation Summary</h2>
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{companyName}</p>
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    {getRatingIcon()}
                    <p className={`text-4xl font-bold ${
                      result.ratingColor === 'green' ? 'text-green-600 dark:text-green-400' :
                      result.ratingColor === 'red' ? 'text-red-600 dark:text-red-400' :
                      'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {result.rating}
                    </p>
                  </div>
                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                  Fair Value: ₹{result.avgFairValue.toLocaleString('en-IN')}
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Current Price: ₹{currentPrice.toLocaleString('en-IN')}
                </p>
                <div className={`mt-4 p-3 rounded-lg ${
                  result.ratingColor === 'green' ? 'bg-green-50 dark:bg-green-900/20' :
                  result.ratingColor === 'red' ? 'bg-red-50 dark:bg-red-900/20' :
                  'bg-yellow-50 dark:bg-yellow-900/20'
                }`}>
                  <p className="text-sm font-semibold">
                    {Number(result.upsidePercent) > 0 ? 'Potential Upside: ' : 'Potential Downside: '}
                    <span className={Number(result.upsidePercent) > 0 ? 'text-green-600' : 'text-red-600'}>
                      {Math.abs(Number(result.upsidePercent))}%
                    </span>
                  </p>
                </div>
              </div>
            </Card>
            )}

            {/* Current Metrics */}
            {!result.error && (
              <Card className="glass p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Current Valuation Metrics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">P/E Ratio</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{result.currentPE}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Industry: {industryPE}</p>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">P/B Ratio</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{result.currentPB}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">&lt;3 is good</p>
                </div>
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">PEG Ratio</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{result.pegRatio}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">&lt;1 is ideal</p>
                </div>
              </div>
            </Card>
            )}

            {/* Fair Value Comparisons */}
            {!result.error && (
              <Card className="glass p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Fair Value by Different Methods</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Industry P/E Method</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Based on sector average P/E</p>
                  </div>
                  <p className="text-xl font-bold text-gray-800 dark:text-gray-100">₹{result.fairValuePE.toLocaleString('en-IN')}</p>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-100">PEG Method</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">P/E justified by growth rate</p>
                  </div>
                  <p className="text-xl font-bold text-gray-800 dark:text-gray-100">₹{result.fairValuePEG.toLocaleString('en-IN')}</p>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-gray-100">Graham Number</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Conservative value investing formula</p>
                  </div>
                  <p className="text-xl font-bold text-gray-800 dark:text-gray-100">₹{result.grahamNumber.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </Card>
            )}

            {/* Key Insights */}
            {!result.error && (
              <Card className="glass p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Key Insights</h3>
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-start space-x-2">
                  <span className="text-blue-600 dark:text-blue-400 font-bold">•</span>
                  <p>
                    <strong>P/E Ratio ({result.currentPE}):</strong> {Number(result.currentPE) < industryPE ? 'Lower than industry average - potentially undervalued' : Number(result.currentPE) > industryPE ? 'Higher than industry average - may be overvalued' : 'In line with industry average'}
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-purple-600 dark:text-purple-400 font-bold">•</span>
                  <p>
                    <strong>PEG Ratio ({result.pegRatio}):</strong> {Number(result.pegRatio) < 1 ? 'Excellent - stock is cheap relative to growth' : Number(result.pegRatio) < 1.5 ? 'Good - fairly valued for the growth rate' : 'High - may be expensive for the growth offered'}
                  </p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">•</span>
                  <p>
                    <strong>ROE ({roe}%):</strong> {roe > 15 ? 'Strong - company efficiently uses shareholder money' : 'Below average - check if it\'s improving'}
                  </p>
                </div>
              </div>
            </Card>
            )}

          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
}
